import type { FallbackProps } from '@gik/core/api/BaseAPIConfig';
import { ISRRevalidateTimeout, baseFallback } from '@gik/core/api/BaseAPIConfig';
import { catchSSRError } from '@gik/core/utils/catchSSRError';
import { measureSSRPerformance } from '@gik/core/utils/measureSSRPerformance';
import { getRelatedArticlesForInkindPageFilters, relatedArticlesNumItems } from '@gik/ui-legacy/gik/RelatedArticles';
import { WishlistPage } from '@gik/wishlist-page/components/WishlistPage';
import { getInkind } from '@/api/inkinds/inkind';
import { getWishlistProducts } from '@/api/inkinds/wishlistProducts';
import { getPostsRelated } from '@/api/posts/postsRelated';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import type { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { SWRConfig } from 'swr';

const WishlistPageWrapper = ({ fallback }: FallbackProps<unknown>) => {
  const router = useRouter();

  return (
    <SWRConfig value={{ fallback }}>
      <WishlistPage inkindRouteId={router.query.id as string} />
    </SWRConfig>
  );
};

export default WishlistPageWrapper;

WishlistPageWrapper.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

async function _getStaticProps(context: GetServerSidePropsContext) {
  const inkindRouteId = context.params.id as string;

  const [inkindResponse, wishlistProducts] = await Promise.all([
    getInkind(inkindRouteId),
    getWishlistProducts(inkindRouteId),
  ]);

  const inkindPage = await inkindResponse.json;

  // redirect to correct url if the inkindRouteId did not match (means a slug was used)
  if (inkindPage.routeId !== inkindRouteId) {
    return {
      redirect: {
        destination: `/inkinds/${inkindPage.routeId}/${inkindPage.slug}`,
        permanent: true,
      },
    };
  }

  const situationSlugs = inkindRouteId && inkindPage?.inkindSituations?.map(s => s.slug);

  const relatedArticles = await getPostsRelated({
    relationFilters: getRelatedArticlesForInkindPageFilters(situationSlugs),
    perPage: relatedArticlesNumItems,
  });

  return baseFallback(
    {
      [inkindResponse.key]: inkindPage,
      [relatedArticles.key]: await relatedArticles.json,
      [wishlistProducts.key]: await wishlistProducts.json,
    },
    undefined,
    ISRRevalidateTimeout.extraSlow
  );
}

export const getStaticProps = catchSSRError(measureSSRPerformance(_getStaticProps, 'Wishlist Page'));

export async function getStaticPaths() {
  // TODO: consider adding some popular pages to be cached at build time
  return { paths: [], fallback: 'blocking' };
}
